import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Check, X, Sparkles, ArrowRight, ArrowLeft } from "lucide-react";
import { AuroraBackground } from "@/components/aurora-background";
import { SectionEyebrow } from "@/components/section-eyebrow";
import { GlassOrb } from "@/components/glass-orb";
import { WebsiteCta } from "@/components/website-cta";
import { CaseStudiesSection } from "@/components/case-studies-section";
import { FaqSection } from "@/components/faq-section";
import { getCaseStudyBySlug, getLatestCaseStudies } from "@/lib/case-studies.functions";
import type { CaseStudy } from "@/lib/sanity";
import { comparisons, comparisonsBySlug, type Comparison } from "@/lib/comparisons-data";
import { seo } from "@/lib/seo";
import { article, breadcrumb, faqPage } from "@/lib/structured-data";
import { SITE_URL } from "@/lib/seo-constants";

function altTitle(d: Comparison) {
  return (
    d.alternativeTitle ??
    `The Best ${d.competitor} Alternative for GEO/AEO in 2026 — Axy.digital vs ${d.competitor}`
  );
}
function altHeading(d: Comparison) {
  return d.alternativeHeading ?? `Why Axy.digital Is the Best ${d.competitor} Alternative`;
}
function altIntro(d: Comparison): string[] {
  if (d.alternativeIntro) return d.alternativeIntro;
  return [
    `If you're searching for a ${d.competitor} alternative, you're in the right place. Axy.digital is the direct answer for teams evaluating ${d.competitor} alternatives or scoping ${d.competitor} competitors — an AI-native platform that runs your full SEO and GEO workflow end to end, not just one slice of it.`,
    `Where ${d.competitor} covers a narrow surface, Axy.digital detects demand, generates GEO/AEO/SEO content, publishes to LinkedIn and X, and measures what moved — the whole marketing loop on autopilot.`,
    `That's why teams researching ${d.competitor} competitors keep landing on Axy.digital: they want an engine that ships results, not another tool to configure.`,
  ];
}
function altDescription(d: Comparison) {
  return (
    d.alternativeMetaDescription ??
    `The best ${d.competitor} alternative for lean teams. ${d.verdictLine ?? d.metaDescription}`
  );
}


type LoaderData = { data: Comparison; caseStudies: CaseStudy[] };

const CONDUCTTR_CASE_STUDY_SLUG = "how-conducttr-germany-streamlined-marketing-with-axy";
const CONDUCTTR_CASE_STUDY_URL =
  "https://www.axy.digital/case-studies/how-conducttr-germany-streamlined-marketing-with-axy";

export const Route = createFileRoute("/comparisons/$slug")({
  loader: async ({ params }): Promise<LoaderData> => {
    const data = comparisonsBySlug[params.slug];
    if (!data) throw notFound();
    const [caseStudies, conducttr] = await Promise.all([
      getLatestCaseStudies(),
      getCaseStudyBySlug({ data: { slug: CONDUCTTR_CASE_STUDY_SLUG } }),
    ]);
    if (conducttr) {
      const { slug: _, ...rest } = conducttr;
      caseStudies[0] = { ...rest, externalUrl: CONDUCTTR_CASE_STUDY_URL };
    }
    return { data, caseStudies };
  },
  head: ({ loaderData, params }) => {
    const d = loaderData?.data;
    if (!d) {
      return seo({
        title: "Comparison not found",
        description: "The comparison you're looking for doesn't exist or has been moved.",
        path: `/comparisons/${params.slug}`,
        noindex: true,
      });
    }
    const title = altTitle(d);
    const description = altDescription(d);
    return seo({
      title,
      description,
      path: `/comparisons/${d.slug}`,
      type: "article",
      jsonLd: [
        article({
          headline: title,
          description,
          url: `${SITE_URL}/comparisons/${d.slug}`,
          type: "Article",
        }),
        breadcrumb([
          { name: "Home", url: "/" },
          { name: "Comparisons", url: "/comparisons" },
          { name: d.competitor, url: `/comparisons/${d.slug}` },
        ]),
        faqPage(d.faq.map((f) => ({ q: f.question, a: f.answer }))),
      ],
    });

  },
  errorComponent: ({ error }) => (
    <section className="px-6 py-24 text-center text-white/70">{error.message}</section>
  ),
  notFoundComponent: () => (
    <section className="px-6 py-24 text-center">
      <p className="text-white/70 mb-4">Comparison not found.</p>
      <Link to="/comparisons" className="text-pink-soft underline">
        Back to all comparisons
      </Link>
    </section>
  ),
  component: ComparisonPage,
});

function ComparisonPage() {
  const { data, caseStudies } = Route.useLoaderData() as LoaderData;
  return (
    <article className="text-white">
      <Hero d={data} />
      <AlternativeAnswer d={data} />
      <QuickCompare d={data} />
      <Verdict d={data} />
      <CoreDifference d={data} />
      <FeatureTable d={data} />
      <BestFit d={data} />
      <CaseStudiesSection items={caseStudies} />
      <FaqSection
        items={data.faq}
        title={`Axy.digital vs ${data.competitor}: FAQ`}
      />
      <OtherAlternatives d={data} />
      <Final d={data} />
    </article>
  );
}

function AlternativeAnswer({ d }: { d: Comparison }) {
  return (
    <section className="px-6 py-20 border-t border-white/5">
      <div className="max-w-4xl mx-auto">
        <SectionEyebrow>Looking for a {d.competitor} alternative?</SectionEyebrow>
        <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-6 text-balance text-gradient">
          {altHeading(d)}
        </h2>
        <div className="space-y-4">
          {altIntro(d).map((p, i) => (
            <p key={i} className="text-white/75 text-lg leading-relaxed">
              {p}
            </p>
          ))}
        </div>
      </div>
    </section>
  );
}

function OtherAlternatives({ d }: { d: Comparison }) {
  const siblings = comparisons.filter((c) => c.slug !== d.slug);
  return (
    <section className="px-6 py-24 border-t border-white/5">
      <div className="max-w-7xl mx-auto">
        <SectionEyebrow>Looking for other alternatives?</SectionEyebrow>
        <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-3 text-balance">
          Compare Axy.digital to more {d.competitor} competitors.
        </h2>
        <p className="text-white/65 mb-10 max-w-2xl">
          Side-by-side breakdowns of every AI marketing, GEO, SEO, and social platform
          teams typically evaluate alongside {d.competitor}.
        </p>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {siblings.map((c) => (
            <Link
              key={c.slug}
              to="/comparisons/$slug"
              params={{ slug: c.slug }}
              className="group relative glass-card p-5 hover-lift hover:-translate-y-1 hover:border-white/25 transition-all duration-300"
            >
              <p className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-2">
                {c.competitor} alternative
              </p>
              <h3 className="text-base font-semibold text-white mb-2">
                Axy.digital vs {c.competitor}
              </h3>
              <span className="inline-flex items-center gap-1 text-xs text-pink-soft group-hover:gap-2 transition-all">
                Read comparison <ArrowRight className="size-3.5" />
              </span>
            </Link>
          ))}
        </div>
        <div className="mt-12">
          <Link
            to="/products/$slug"
            params={{ slug: "geo-aeo-seo-autopilot" }}
            className="inline-flex items-center gap-2 text-sm text-pink-soft hover:text-white transition-colors"
          >
            <ArrowLeft className="size-4" />
            Back to Axy.digital GEO/AEO/SEO Autopilot
          </Link>
        </div>
      </div>
    </section>
  );
}


function Hero({ d }: { d: Comparison }) {
  return (
    <section className="relative px-6 pt-20 pb-20 overflow-hidden">
      <AuroraBackground />
      <div className="relative max-w-5xl mx-auto">
        <SectionEyebrow>Comparison</SectionEyebrow>
        <h1 className="text-4xl md:text-6xl font-semibold tracking-tight mb-6 text-balance">
          {d.pageTitle}
        </h1>
        <p className="text-lg md:text-xl text-white/90 max-w-3xl mb-6 text-pretty">
          <span className="text-[11px] uppercase tracking-[0.22em] text-pink-soft mr-2 align-middle">
            Verdict
          </span>
          {d.verdictLine}
        </p>
        <p className="text-lg text-white/70 max-w-3xl mb-6">{d.heroSubtitle}</p>
        <div className="space-y-3 max-w-3xl">
          {d.heroIntro.map((p, i) => (
            <p key={i} className="text-white/60 leading-relaxed">
              {p}
            </p>
          ))}
        </div>
        <div className="mt-10">
          <WebsiteCta showForm={false} source="comparison" />
        </div>
      </div>
    </section>
  );
}

function QuickCompare({ d }: { d: Comparison }) {
  return (
    <section className="px-6 py-20">
      <div className="max-w-6xl mx-auto">
        <SectionEyebrow>At a glance</SectionEyebrow>
        <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-8 text-balance">
          Axy.digital vs {d.competitor}, side by side.
        </h2>
        <div className="glass-card overflow-hidden">
          <div className="grid grid-cols-12 px-5 md:px-7 py-4 border-b border-white/10 text-[11px] uppercase tracking-[0.22em] text-white/50">
            <div className="col-span-4 md:col-span-3"></div>
            <div className="col-span-4 md:col-span-4 text-pink-soft">Axy.digital</div>
            <div className="col-span-4 md:col-span-5">{d.competitor}</div>
          </div>
          {d.quickCompare.map((row, i) => (
            <div
              key={row.row}
              className={`grid grid-cols-12 px-5 md:px-7 py-4 text-sm items-start ${
                i % 2 ? "bg-white/[0.02]" : ""
              }`}
            >
              <div className="col-span-4 md:col-span-3 text-white/90 font-medium pr-3">
                {row.row}
              </div>
              <div className="col-span-4 md:col-span-4 text-white pr-3">{row.axy}</div>
              <div className="col-span-4 md:col-span-5 text-white/65">{row.other}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Verdict({ d }: { d: Comparison }) {
  return (
    <section className="px-6 py-24">
      <div className="max-w-6xl mx-auto">
        <SectionEyebrow>Quick verdict</SectionEyebrow>
        <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-10 text-balance">
          Choose the engine that fits how you operate.
        </h2>
        <div className="grid md:grid-cols-2 gap-5">
          <div
            className="relative glass-card p-6 md:p-7 overflow-hidden"
            style={{
              boxShadow: "0 24px 80px rgba(217,70,239,0.25), 0 0 0 1px rgba(217,70,239,0.18) inset",
            }}
          >
            <div
              aria-hidden
              className="absolute -top-20 -right-12 size-60 rounded-full blur-3xl opacity-60"
              style={{
                background: "radial-gradient(circle, rgba(217,70,239,0.5), transparent 60%)",
              }}
            />
            <p className="relative text-[11px] uppercase tracking-[0.22em] text-pink-soft mb-5 inline-flex items-center gap-2">
              <Sparkles className="size-3.5" /> Choose Axy.digital if you want
            </p>
            <ul className="relative space-y-3">
              {d.verdict.map((r) => (
                <li key={r.axy} className="flex items-start gap-3 text-sm text-white/95">
                  <span
                    className="grid place-items-center size-5 rounded-full mt-0.5 shrink-0 border border-white/15"
                    style={{
                      background:
                        "linear-gradient(135deg, rgba(244,114,208,0.9), rgba(123,63,242,0.9))",
                    }}
                  >
                    <Check className="size-3 text-white" />
                  </span>
                  {r.axy}
                </li>
              ))}
            </ul>
          </div>
          <div className="glass-card p-6 md:p-7 opacity-90">
            <p className="text-[11px] uppercase tracking-[0.22em] text-white/50 mb-5">
              Choose {d.competitor} if you want
            </p>
            <ul className="space-y-3">
              {d.verdict.map((r) => (
                <li key={r.other} className="flex items-start gap-3 text-sm text-white/70">
                  <span className="grid place-items-center size-5 rounded-full bg-white/5 border border-white/10 mt-0.5 shrink-0">
                    <X className="size-3 text-white/45" />
                  </span>
                  {r.other}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function CoreDifference({ d }: { d: Comparison }) {
  return (
    <section className="px-6 py-24">
      <div className="max-w-4xl mx-auto">
        <SectionEyebrow>The core difference</SectionEyebrow>
        <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-8 text-balance text-gradient">
          {d.coreHeading}
        </h2>
        <div className="space-y-4">
          {d.coreBody.map((p, i) => (
            <p key={i} className="text-white/75 text-lg leading-relaxed">
              {p}
            </p>
          ))}
        </div>
      </div>
    </section>
  );
}

function FeatureTable({ d }: { d: Comparison }) {
  return (
    <section className="px-6 py-24">
      <div className="max-w-6xl mx-auto">
        <SectionEyebrow>Feature comparison</SectionEyebrow>
        <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-10 text-balance">
          What each platform actually does.
        </h2>
        <div className="glass-card overflow-hidden">
          <div className="grid grid-cols-12 px-5 md:px-7 py-4 border-b border-white/10 text-[11px] uppercase tracking-[0.22em] text-white/50">
            <div className="col-span-6 md:col-span-5">Capability</div>
            <div className="col-span-3 md:col-span-3 text-pink-soft">Axy.digital</div>
            <div className="col-span-3 md:col-span-4">{d.competitor}</div>
          </div>
          {d.features.map((row, i) => (
            <div
              key={row.capability}
              className={`grid grid-cols-12 px-5 md:px-7 py-4 text-sm items-start ${
                i % 2 ? "bg-white/[0.02]" : ""
              }`}
            >
              <div className="col-span-6 md:col-span-5 text-white/90 font-medium pr-3">
                {row.capability}
              </div>
              <div className="col-span-3 md:col-span-3 text-white pr-3">{row.axy}</div>
              <div className="col-span-3 md:col-span-4 text-white/65">{row.other}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function BestFit({ d }: { d: Comparison }) {
  return (
    <section className="px-6 py-24">
      <div className="max-w-6xl mx-auto">
        <SectionEyebrow>Best fit</SectionEyebrow>
        <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-10 text-balance">
          Which one is right for your team?
        </h2>
        <div className="grid md:grid-cols-2 gap-5">
          <div
            className="glass-card p-7"
            style={{
              boxShadow: "0 24px 80px rgba(217,70,239,0.2), 0 0 0 1px rgba(217,70,239,0.18) inset",
            }}
          >
            <p className="text-[11px] uppercase tracking-[0.22em] text-pink-soft mb-3">
              Axy.digital is a better fit if
            </p>
            <p className="text-white/95 leading-relaxed">{d.bestFitAxy}</p>
          </div>
          <div className="glass-card p-7">
            <p className="text-[11px] uppercase tracking-[0.22em] text-white/50 mb-3">
              {d.competitor} may be a fit if
            </p>
            <p className="text-white/80 leading-relaxed">{d.bestFitOther}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function Final({ d }: { d: Comparison }) {
  return (
    <section className="relative px-6 py-28 overflow-hidden">
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, transparent, rgba(43,15,74,0.7) 30%, rgba(11,6,20,0.95)), radial-gradient(60% 60% at 50% 100%, rgba(217,70,239,0.35), transparent 60%)",
        }}
      />
      <div
        aria-hidden
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 opacity-80 pointer-events-none"
      >
        <GlassOrb size="lg" />
      </div>
      <div className="relative max-w-3xl mx-auto text-center">
        <h2 className="text-4xl md:text-6xl font-semibold tracking-tight text-white text-balance mb-5">
          {d.finalHeading}
        </h2>
        {d.finalBody && (
          <p className="text-lg text-white/75 max-w-2xl mx-auto mb-10 text-pretty">{d.finalBody}</p>
        )}
        <WebsiteCta align="center" showPrimary={false} source="comparison" />
      </div>
    </section>
  );
}
